import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  ProgressReportByProcessPageConfig,
  toStoredColumnState,
} from '../../../../../domain/entity/version-report/VersionReportTemplateEntity'
import {
  ProgressReportByProcessPageContent,
  ProgressReportQualitativeContent,
} from '../../../../../domain/entity/version-report/VersionReportVersionEntity'
import { ProgressReportTable } from '../table/ProgressReportTable'
import { useProjectPlanSnapshotRepository } from '../../../../../services/adaptors/projectPlanSnapshotRepositoryAdaptor'
import { ProgressValue } from '../../../../../domain/value-object/ProjectPlanSnapshot'
import { aggregatedValueService } from '../../../../../domain/value-object/AggregatedValueVO'
import {
  AggregateField,
  WbsItemType,
} from '../../../../../domain/entity/WbsItemEntity'
import { WorkloadUnit } from '../../../../../lib/functions/workload'
import { EditConfigButton } from '.'
import { ProgressReportByProcessConfigDialog } from '../dialog/ProgressReportByProcessConfigDialog'
import DateVO from '../../../../../vo/DateVO'
import { DISPLAY_DATE_FORMAT_WITH_YEAR_DAY } from '../../../../../utils/date'
import { useWorkloadUnit } from '../../../../hooks/useWorkloadUnit'
import { useDialogState } from '../../../../components/dialogs/useDialogState'
import { useColumnSetting } from '../../../../containers/BulkSheetView/components/columnSelector/useColumnSetting'
import { ColumnState, GridApi, GridReadyEvent } from 'ag-grid-community'
import ProgressReportGridWbsHeader from '../header/ProgressReportGridConfigEditHeader'
import { ROW_HEIGHT } from '../../../../containers/BulkSheet'
import { ColumnConfigPopper } from '../dialog/ColumnConfigPopper'
import { useProcessReportColumnDef } from '../../hooks/columnDef'

type ProgressReportByProcessProps = {
  snapshotUuid: string
  editing: boolean
  config: ProgressReportByProcessPageConfig
  content: ProgressReportByProcessPageContent
  updatePageConfig: <
    K extends keyof ProgressReportByProcessPageConfig,
    V extends ProgressReportByProcessPageConfig[K],
  >(
    path: K,
    value: V
  ) => void
  updatePageContent: <
    K extends keyof ProgressReportByProcessPageContent,
    V extends ProgressReportByProcessPageContent[K],
  >(
    path: K,
    value: V
  ) => void
  callbackWhenStartLoading?: () => void
  callbackWhenFinishLoading?: () => void
}
export const ProgressReportByProcess = ({
  snapshotUuid,
  editing,
  config,
  content,
  updatePageConfig,
  updatePageContent,
  callbackWhenStartLoading,
  callbackWhenFinishLoading,
}: ProgressReportByProcessProps) => {
  const [progressValues, setProgressValues] = useState<ProgressValue[]>([])
  const { pivotByProjectPlanUuids } = useProjectPlanSnapshotRepository()
  const { convertWorkloadFromHourToSelectedUnit } = useWorkloadUnit(
    config.workloadUnit || WorkloadUnit.HOUR
  )
  const prevTableRowsRef = useRef(config.tableRows)
  const prevSnapshotUuidRef = useRef(snapshotUuid)

  useEffect(() => {
    const hasTableRowsChanged =
      JSON.stringify(prevTableRowsRef.current) !==
      JSON.stringify(config.tableRows)
    const hasSnapshotUuidChanged = prevSnapshotUuidRef.current !== snapshotUuid

    if (hasTableRowsChanged || hasSnapshotUuidChanged) {
      setProgressValues([])
    }

    const fn = async () => {
      callbackWhenStartLoading?.()
      const response = await pivotByProjectPlanUuids(
        snapshotUuid,
        config.tableRows
      )
      setProgressValues(response)
      callbackWhenFinishLoading?.()

      prevTableRowsRef.current = config.tableRows
      prevSnapshotUuidRef.current = snapshotUuid
    }
    fn()
  }, [
    config.tableRows,
    pivotByProjectPlanUuids,
    snapshotUuid,
    callbackWhenStartLoading,
    callbackWhenFinishLoading,
  ])
  const tableContents = useMemo(
    () =>
      progressValues.map(v => {
        const qualitativeContent = content.tableContents.find(
          u => u.uuid === v.uuid
        )
        const aggregateField =
          config.aggregateField || AggregateField.WBS_ITEM_COUNT
        const aggregateTarget = config.aggregateTarget || WbsItemType.TASK
        return {
          uuid: v.uuid,
          name: v.name,
          status: qualitativeContent?.status || 'SUNNY',
          comment: qualitativeContent?.comment || '',
          scheduledProgressRate:
            aggregatedValueService.getFormattedRatePercentage(
              v.planned,
              v.total,
              aggregateTarget,
              aggregateField
            ),
          progressRate: aggregatedValueService.getFormattedRatePercentage(
            v.completed,
            v.total,
            aggregateTarget,
            aggregateField
          ),
          total: aggregatedValueService.getFormattedWithUnitConversion(
            v.total,
            aggregateTarget,
            aggregateField,
            convertWorkloadFromHourToSelectedUnit
          ),
          planned: aggregatedValueService.getFormattedWithUnitConversion(
            v.planned,
            aggregateTarget,
            aggregateField,
            convertWorkloadFromHourToSelectedUnit
          ),
          completed: aggregatedValueService.getFormattedWithUnitConversion(
            v.completed,
            aggregateTarget,
            aggregateField,
            convertWorkloadFromHourToSelectedUnit
          ),
          delay: aggregatedValueService.getFormattedWithUnitConversion(
            v.delay,
            aggregateTarget,
            aggregateField,
            convertWorkloadFromHourToSelectedUnit
          ),
          remaining: aggregatedValueService.getFormattedWithUnitConversion(
            v.remaining,
            aggregateTarget,
            aggregateField,
            convertWorkloadFromHourToSelectedUnit
          ),
          wbsItemStatus: v.wbsItemStatus,
          team: v.team,
          progressRateDiff: aggregatedValueService.getFormattedRatePercentage(
            v.plannedCompletedDiff,
            v.total,
            aggregateTarget,
            aggregateField
          ),
          scheduledStartDate: new DateVO(v.scheduledDate?.startDate).format(
            DISPLAY_DATE_FORMAT_WITH_YEAR_DAY
          ),
          scheduledEndDate: new DateVO(v.scheduledDate?.endDate).format(
            DISPLAY_DATE_FORMAT_WITH_YEAR_DAY
          ),
          maxChildScheduledEndDate: new DateVO(
            v.maxChildScheduledEndDate
          ).format(DISPLAY_DATE_FORMAT_WITH_YEAR_DAY),
          preceding: aggregatedValueService.getFormattedWithUnitConversion(
            v.preceding,
            aggregateTarget,
            aggregateField,
            convertWorkloadFromHourToSelectedUnit
          ),
          unplanned: aggregatedValueService.getFormattedWithUnitConversion(
            v.unplanned,
            aggregateTarget,
            aggregateField,
            convertWorkloadFromHourToSelectedUnit
          ),
        }
      }),
    [
      content.tableContents,
      progressValues,
      config.aggregateField,
      config.aggregateTarget,
      convertWorkloadFromHourToSelectedUnit,
    ]
  )
  const qualitativeContents: ProgressReportQualitativeContent[] = useMemo(
    () =>
      tableContents.map(v => ({
        uuid: v.uuid,
        status: v.status,
        comment: v.comment,
      })),
    [tableContents]
  )
  const updateTableContent = useCallback(
    (v: ProgressReportQualitativeContent, index: number) => {
      const newContent = qualitativeContents.map((u, i) => {
        if (i !== index) return u
        return v
      })
      updatePageContent('tableContents', newContent)
    },
    [qualitativeContents, updatePageContent]
  )
  const updateConfig = useCallback(
    (targetUuids: string[]) => {
      updatePageConfig('tableRows', targetUuids)
    },
    [updatePageConfig]
  )
  const [open, openDialog, closeDialog] = useDialogState()
  const gridApi = useRef<GridApi | null>(null)
  const columnSettingDialog = useColumnSetting()
  const ref = useRef<HTMLDivElement>(null)

  const onGridReady = useCallback((e: GridReadyEvent) => {
    gridApi.current = e.api
  }, [])

  const updateColumnStateConfig = useCallback(
    (state: ColumnState[]) => {
      const storedState = state.map(v => toStoredColumnState(v))
      updatePageConfig('columnState', storedState)
    },
    [updatePageConfig]
  )

  const columns = useProcessReportColumnDef({
    editing,
    updateTableContent,
  })

  useEffect(() => {
    gridApi.current?.resetRowHeights()
  }, [config.rowHeight])
  return (
    <>
      {editing && (
        <ProgressReportGridWbsHeader
          aggregateTarget={config.aggregateTarget}
          onChangeAggregateTarget={v => updatePageConfig('aggregateTarget', v)}
          onChangeAggregateField={v => updatePageConfig('aggregateField', v)}
          aggregateField={config.aggregateField}
          onChangeWorkloadUnit={v => updatePageConfig('workloadUnit', v)}
          workloadUnit={config.workloadUnit}
          rowHeight={config.rowHeight || ROW_HEIGHT.MEDIUM}
          onChangeHeight={v => updatePageConfig('rowHeight', v)}
          onClickColumnSettingButton={columnSettingDialog.toggle}
          columnSettingOpen={columnSettingDialog.isOpen}
        />
      )}
      <ProgressReportTable
        ref={ref}
        columns={columns}
        editing={editing}
        contents={tableContents}
        onGridReady={onGridReady}
        rowHeight={config.rowHeight || ROW_HEIGHT.MEDIUM}
        updateColumnStateConfig={updateColumnStateConfig}
        columnState={config.columnState}
      />
      <EditConfigButton editing={editing} onClick={openDialog} />
      <ProgressReportByProcessConfigDialog
        open={open}
        onClose={closeDialog}
        updateConfig={updateConfig}
        config={config}
        snapshotUuid={snapshotUuid}
      />
      {editing && columnSettingDialog.isOpen && (
        <ColumnConfigPopper
          anchorEl={columnSettingDialog.anchorEl as Element | undefined}
          onClose={columnSettingDialog.close}
          gridApi={gridApi.current!}
          updateConfig={updateColumnStateConfig}
          height={ref.current?.offsetHeight}
        />
      )}
    </>
  )
}
